import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Obtém a chave do mês a partir da data da transação (formato: YYYY-MM)
 */
const getMonthKey = (transactionDate) => {
  const date = transactionDate?.toDate ? transactionDate.toDate() : new Date(transactionDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Atualiza o agregado mensal de transações quando uma transação é criada
 */
export const updateTransactionsAggregationOnCreate = async (transactionData) => {
  try {
    if (!transactionData || !transactionData.id || !transactionData.data) {
      return;
    }

    const transactionDate = transactionData.data;
    const monthKey = getMonthKey(transactionDate);
    const transactionsAggregateRef = doc(db, 'agregadoTransacoes', monthKey);
    const transactionsAggregateDoc = await getDoc(transactionsAggregateRef);
    
    // Mapear campos para português, removendo campos em inglês
    const transactionEntry = {
      id: transactionData.id,
      tipo: transactionData.tipo,
      idPet: transactionData.idPet || null,
      valor: transactionData.valor || 0,
      idTipoTransacao: transactionData.idTipoTransacao || null,
      descricao: transactionData.descricao || '',
      data: transactionData.data instanceof Date ? Timestamp.fromDate(transactionData.data) : transactionData.data,
      criadoEm: transactionData.criadoEm || null,
      criadoPor: transactionData.criadoPor || null,
      nomePet: transactionData.nomePet || null,
      nomeTipoTransacao: transactionData.nomeTipoTransacao || null,
      idAgendamento: transactionData.idAgendamento || null
    };
    
    if (transactionsAggregateDoc.exists()) {
      const currentData = transactionsAggregateDoc.data();
      const transactions = currentData.transacoes || [];
      
      const existingIndex = transactions.findIndex(t => t.id === transactionData.id);
      
      if (existingIndex === -1) {
        transactions.push(transactionEntry);
        // Ordenar por data (mais recente primeiro)
        transactions.sort((a, b) => {
          const dateA = a.data?.toDate ? a.data.toDate() : new Date(a.data);
          const dateB = b.data?.toDate ? b.data.toDate() : new Date(b.data);
          return dateB.getTime() - dateA.getTime();
        });
        
        await updateDoc(transactionsAggregateRef, {
          transacoes: transactions,
          quantidadeTotal: transactions.length,
          ultimaAtualizacao: Timestamp.now()
        });
      } else {
        // Transação já existe no agregado, atualizando
        transactions[existingIndex] = transactionEntry;
        transactions.sort((a, b) => {
          const dateA = a.data?.toDate ? a.data.toDate() : new Date(a.data);
          const dateB = b.data?.toDate ? b.data.toDate() : new Date(b.data);
          return dateB.getTime() - dateA.getTime();
        });
        await updateDoc(transactionsAggregateRef, {
          transacoes: transactions,
          quantidadeTotal: transactions.length,
          ultimaAtualizacao: Timestamp.now()
        });
      }
    } else {
      // Criar novo agregado mensal
      const date = transactionDate?.toDate ? transactionDate.toDate() : new Date(transactionDate);
      await setDoc(transactionsAggregateRef, {
        ano: date.getFullYear(),
        mes: date.getMonth() + 1,
        transacoes: [transactionEntry],
        quantidadeTotal: 1,
        ultimaAtualizacao: Timestamp.now()
      });
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o agregado mensal de transações quando uma transação é editada
 * Se a data mudou de mês, remove do agregado antigo e adiciona no novo
 */
export const updateTransactionsAggregationOnUpdate = async (oldTransactionData, newTransactionData) => {
  try {
    if (!newTransactionData || !newTransactionData.id || !newTransactionData.data) {
      return;
    }

    const newTransactionDate = newTransactionData.data;
    const oldTransactionDate = oldTransactionData?.data;
    const newMonthKey = getMonthKey(newTransactionDate);
    const oldMonthKey = oldTransactionDate ? getMonthKey(oldTransactionDate) : null;
    
    // Se mudou de mês, remover do agregado antigo e adicionar no novo
    if (oldMonthKey && oldMonthKey !== newMonthKey) {
      // Remover do agregado antigo
      const oldAggregateRef = doc(db, 'agregadoTransacoes', oldMonthKey);
      const oldAggregateDoc = await getDoc(oldAggregateRef);
      
      if (oldAggregateDoc.exists()) {
        const oldData = oldAggregateDoc.data();
        const oldTransactions = oldData.transacoes || [];
        const filteredTransactions = oldTransactions.filter(t => t.id !== newTransactionData.id);
        
        if (filteredTransactions.length !== oldTransactions.length) {
          if (filteredTransactions.length > 0) {
            await updateDoc(oldAggregateRef, {
              transacoes: filteredTransactions,
              quantidadeTotal: filteredTransactions.length,
              ultimaAtualizacao: Timestamp.now()
            });
          } else {
            // Se não há mais transações, deletar o agregado mensal
            await updateDoc(oldAggregateRef, {
              transacoes: [],
              quantidadeTotal: 0,
              ultimaAtualizacao: Timestamp.now()
            });
          }
        }
      }
      
      // Adicionar no novo agregado mensal
      await updateTransactionsAggregationOnCreate(newTransactionData);
    } else {
      // Mesmo mês, apenas atualizar no agregado
      const transactionsAggregateRef = doc(db, 'agregadoTransacoes', newMonthKey);
      const transactionsAggregateDoc = await getDoc(transactionsAggregateRef);
      
      if (transactionsAggregateDoc.exists()) {
        const currentData = transactionsAggregateDoc.data();
        const transactions = currentData.transacoes || [];
        
        const transactionIndex = transactions.findIndex(t => t.id === newTransactionData.id);
        
        // Mapear campos para português, removendo campos em inglês
        const transactionEntry = {
          id: newTransactionData.id,
          tipo: newTransactionData.tipo,
          idPet: newTransactionData.idPet || null,
          valor: newTransactionData.valor || 0,
          idTipoTransacao: newTransactionData.idTipoTransacao || null,
          descricao: newTransactionData.descricao || '',
          data: newTransactionData.data instanceof Date ? Timestamp.fromDate(newTransactionData.data) : newTransactionData.data,
          criadoEm: newTransactionData.criadoEm || null,
          criadoPor: newTransactionData.criadoPor || null,
          nomePet: newTransactionData.nomePet || null,
          nomeTipoTransacao: newTransactionData.nomeTipoTransacao || null,
          idAgendamento: newTransactionData.idAgendamento || null
        };
        
        if (transactionIndex !== -1) {
          transactions[transactionIndex] = transactionEntry;
        } else {
          transactions.push(transactionEntry);
        }
        
        // Reordenar por data
        transactions.sort((a, b) => {
          const dateA = a.data?.toDate ? a.data.toDate() : new Date(a.data);
          const dateB = b.data?.toDate ? b.data.toDate() : new Date(b.data);
          return dateB.getTime() - dateA.getTime();
        });
        
        await updateDoc(transactionsAggregateRef, {
          transacoes: transactions,
          quantidadeTotal: transactions.length,
          ultimaAtualizacao: Timestamp.now()
        });
      } else {
        // Agregado não existe, criar novo
        await updateTransactionsAggregationOnCreate(newTransactionData);
      }
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o agregado mensal de transações quando uma transação é deletada
 * Precisa buscar a transação para saber qual mês remover
 */
export const updateTransactionsAggregationOnDelete = async (transactionId, transactionDate) => {
  try {
    if (!transactionId) {
      return;
    }

    // Se não temos a data, precisamos buscar a transação
    let monthKey = null;
    if (transactionDate) {
      monthKey = getMonthKey(transactionDate);
    } else {
      // Buscar a transação para obter a data
      const transactionRef = doc(db, 'transacoes', transactionId);
      const transactionDoc = await getDoc(transactionRef);
      if (transactionDoc.exists()) {
        const transactionData = transactionDoc.data();
        if (transactionData.data) {
          monthKey = getMonthKey(transactionData.data);
        }
      }
    }

    if (!monthKey) {
      // Não foi possível determinar o mês, buscar em todos os agregados mensais
      // Por enquanto, vamos apenas retornar sem erro
      return;
    }

    const transactionsAggregateRef = doc(db, 'agregadoTransacoes', monthKey);
    const transactionsAggregateDoc = await getDoc(transactionsAggregateRef);
    
    if (transactionsAggregateDoc.exists()) {
      const currentData = transactionsAggregateDoc.data();
      const transactions = currentData.transacoes || [];
      
      const filteredTransactions = transactions.filter(t => t.id !== transactionId);
      
      if (filteredTransactions.length !== transactions.length) {
        if (filteredTransactions.length > 0) {
          await updateDoc(transactionsAggregateRef, {
            transacoes: filteredTransactions,
            quantidadeTotal: filteredTransactions.length,
            ultimaAtualizacao: Timestamp.now()
          });
        } else {
          // Se não há mais transações, manter o agregado mas vazio
          await updateDoc(transactionsAggregateRef, {
            transacoes: [],
            quantidadeTotal: 0,
            ultimaAtualizacao: Timestamp.now()
          });
        }
      }
    }
  } catch (error) {
    throw error;
  }
};


